import TimeOffType, { CustomTimeOffType } from 'types/enum/TimeOffType';
import type {
  IOrganizationTimeOffSettings,
  IOrganizationTimeOffSettingsDefinition,
} from 'utils/organization/timeOffSettings/timeOffSettingsTypes';

export const BASE_TIMEOFF_TYPE_IDENTIFIERS: TimeOffType[] = [
  TimeOffType.HOLIDAY,
  TimeOffType.PERSONAL,
  TimeOffType.SICK,
  TimeOffType.VACATION,
  TimeOffType.OTHER,
] as const;

export const CUSTOM_TIMEOFF_TYPE_IDENTIFIERS: CustomTimeOffType[] = [
  CustomTimeOffType.CUSTOM_TIMEOFF_01,
  CustomTimeOffType.CUSTOM_TIMEOFF_02,
  CustomTimeOffType.CUSTOM_TIMEOFF_03,
  CustomTimeOffType.CUSTOM_TIMEOFF_04,
  CustomTimeOffType.CUSTOM_TIMEOFF_05,
] as const;

export type TimeOffTypeIdentifier = TimeOffType | CustomTimeOffType;

export const TIMEOFF_TYPE_IDENTIFIERS: TimeOffTypeIdentifier[] = [
  ...BASE_TIMEOFF_TYPE_IDENTIFIERS,
  ...CUSTOM_TIMEOFF_TYPE_IDENTIFIERS,
] as const;

export function isBaseTimeOffTypeIdentifier(v: unknown): v is TimeOffType {
  return typeof v === 'number' && Number.isInteger(v) && BASE_TIMEOFF_TYPE_IDENTIFIERS.includes(v);
}

export function isCustomTimeOffTypeIdentifier(v: unknown): v is CustomTimeOffType {
  return typeof v === 'number' && Number.isInteger(v) && CUSTOM_TIMEOFF_TYPE_IDENTIFIERS.includes(v);
}

export function isTimeOffTypeIdentifier(v: unknown): v is TimeOffTypeIdentifier {
  return typeof v === 'number' && Number.isInteger(v) && TIMEOFF_TYPE_IDENTIFIERS.includes(v);
}

export const baseTimeOffTypeLabels: Record<TimeOffType, string> = {
  [TimeOffType.HOLIDAY]: 'Holiday',
  [TimeOffType.PERSONAL]: 'Personal',
  [TimeOffType.SICK]: 'Sick',
  [TimeOffType.VACATION]: 'Vacation',
  [TimeOffType.OTHER]: 'Other',
} as const;

export const baseTimeOffTypeAbbreviations: Record<TimeOffType, string> = {
  [TimeOffType.HOLIDAY]: 'HOL',
  [TimeOffType.PERSONAL]: 'PTO',
  [TimeOffType.SICK]: 'SIC',
  [TimeOffType.VACATION]: 'VAC',
  [TimeOffType.OTHER]: 'OTH',
} as const;

export type BaseTimeOffTypeLabel = (typeof baseTimeOffTypeLabels)[TimeOffType];
export type BaseTimeOffTypeAbbreviation = (typeof baseTimeOffTypeAbbreviations)[TimeOffType];

export const baseTimeOffTypeDefinitions: Record<TimeOffType, IOrganizationTimeOffSettingsDefinition> = Object.freeze(
  BASE_TIMEOFF_TYPE_IDENTIFIERS.reduce(
    (acc, type: TimeOffType) => {
      acc[type] = {
        type,
        label: baseTimeOffTypeLabels[type],
        abbreviation: baseTimeOffTypeAbbreviations[type],
      };

      return acc;
    },
    {} as Record<TimeOffType, IOrganizationTimeOffSettingsDefinition>
  )
);

export function getBaseTimeOffTypeDefinition(type: TimeOffType): IOrganizationTimeOffSettingsDefinition {
  if (!isBaseTimeOffTypeIdentifier(type)) {
    throw new Error(`Type ${type} is not a valid TimeOffType identifier`);
  }
  return baseTimeOffTypeDefinitions[type];
}

export function getBaseTimeOffTypeLabel(type: TimeOffType): BaseTimeOffTypeLabel {
  return getBaseTimeOffTypeDefinition(type).label;
}

export function getBaseTimeOffTypeAbbreviation(type: TimeOffType): BaseTimeOffTypeAbbreviation {
  return getBaseTimeOffTypeDefinition(type).abbreviation;
}

export function getTimeOffTypeDefinition(
  type: TimeOffTypeIdentifier,
  timeOffSettings: IOrganizationTimeOffSettings
): IOrganizationTimeOffSettingsDefinition {
  if (isBaseTimeOffTypeIdentifier(type)) {
    return getBaseTimeOffTypeDefinition(type);
  }

  if (isCustomTimeOffTypeIdentifier(type)) {
    const def = timeOffSettings.custom.find((d) => d.type === type);
    if (def) {
      return def;
    } else {
      return getCustomTimeOffTypeDefaultDefinition(type);
    }
  }

  throw new Error(`Invalid time off type identifier: ${type}`);
}

export function getTimeOffTypeLabel(type: TimeOffTypeIdentifier, timeOffSettings: IOrganizationTimeOffSettings) {
  return getTimeOffTypeDefinition(type, timeOffSettings).label;
}

export function getTimeOffTypeAbbreviation(type: TimeOffTypeIdentifier, timeOffSettings: IOrganizationTimeOffSettings) {
  return getTimeOffTypeDefinition(type, timeOffSettings).abbreviation;
}

export function getCustomTimeOffTypeDefaultDefinition(type: CustomTimeOffType): IOrganizationTimeOffSettingsDefinition {
  if (!isCustomTimeOffTypeIdentifier(type)) {
    throw new Error(`Type ${type} is not a valid CustomTimeOffType identifier`);
  }

  const customIndex = CUSTOM_TIMEOFF_TYPE_IDENTIFIERS.indexOf(type) + 1;

  return {
    type,
    label: `Custom ${customIndex}`,
    abbreviation: `CT${customIndex}`,
  };
}
